import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import { EMPTY_STRING } from '@e-socna/empty';
import type { DocumentTypeId, PrintStyleId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { DocumentTemplateInfoForm } from '../../forms/DocumentTemplateInfo';
import { RightDrawer } from '../../components/RightDrawer';
import { Code } from '../../components/Code';
import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';

import type { DocumentTemplatePage_OneDocumentTemplate } from './hooks/useFindOneDocumentTemplateQuery.hook';

export const DocumentTemplateInfoTabPanel = () => {
  const {
    props: {
      routerSettings: { editorModePaths },
      handlers,
    },
    state,
    actions,
  } = useDocumentEditorContext<DocumentTemplatePage_OneDocumentTemplate>();

  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const { documentTypesIdMap, printStylesIdMap } = useReferencesContext();

  const leaveEditionMode = useCallback(
    () => void navigate(editorModePaths.Info ?? PATH_NOT_FOUND),
    [editorModePaths.Info, navigate],
  );

  const documentTemplate = state.editable;
  const {
    codingConfig: templateCodingConfig,
    code: templateCode,
    label,
    document,
    printing: { printSettings },
  } = documentTemplate;

  const { title, typeId } = document;

  const useSubmitFactory = useCallback(
    (callback?: () => void) =>
      (values: DocumentTemplatePage_OneDocumentTemplate) => {
        actions.updateInfo(values);
        void handlers.onSavePrintSettings?.(values.printing.printSettings);
        callback?.();
      },
    [actions, handlers],
  );

  const documentTypeLabel =
    documentTypesIdMap[typeId as DocumentTypeId]?.label ?? 'Inconnu';

  const printStyleLabel =
    printStylesIdMap[printSettings.printStyleId as PrintStyleId]?.label ??
    'Inconnu';

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Informations Générales
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={templateCode} codingConfig={templateCodingConfig} />
            <Typography gutterBottom variant="body1">
              Libellé : {label === EMPTY_STRING ? 'Non défini' : label}
            </Typography>
            <Typography gutterBottom variant="body1">
              Titre : {!title || title === EMPTY_STRING ? 'Non défini' : title}
            </Typography>
            <Typography gutterBottom variant="body1">
              Type de document : {documentTypeLabel}
            </Typography>
          </Box>
        </Paper>
        <Typography gutterBottom variant="h6">
          Options d&apos;impression
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography gutterBottom variant="body1">
              Inclure entêtes de pages :{' '}
              {printSettings.addHeaders ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Inclure pieds de pages :{' '}
              {printSettings.addFooters ? 'Oui' : 'Non'}
            </Typography>
            <Typography gutterBottom variant="body1">
              Style d&apos;impression : {printStyleLabel}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <RightDrawer
        isOpen={currentPath === editorModePaths.InfoEdit}
        onDrawerClose={leaveEditionMode}
      >
        <DocumentTemplateInfoForm
          initialValues={documentTemplate}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          showCancelButton
          title="Informations Générales"
          useSubmitFactory={useSubmitFactory}
        />
      </RightDrawer>
    </>
  );
};
